<script setup lang="ts">
import { computed } from 'vue';
import { UserStatus } from '@enums/userStatus';
import type { SearchCommentTimeline } from '@models/searchCommentTimeline';
import IconCaretRight from '@icons/ph_caret-right.svg';
import Icon from '@components/Icon.vue';
import Typography from '@components/Typography.vue';
import UserPicture from '@components/UserPicture.vue';
import { getShortReadableDateTime } from '@tools/date';
import { trans } from '@elements/translation';
import UserAuth from '@tools/user/auth';
import type { User as AuthUser } from '@models/auth/user';

const props = defineProps<{ lastComment: SearchCommentTimeline | null }>();

const currentUser = computed<AuthUser | undefined>(() => UserAuth.getUser());

const author = computed<string>(() => {
  if (props.lastComment === null) {
    return '';
  }

  if (props.lastComment?.impersonated) {
    return trans('selection_item.comment.account_manager', { firstname: props.lastComment?.pipedrive_user?.firstname, company_name: window.COMPANY_NAME });
  }

  if (props.lastComment?.author.status !== UserStatus.ACTIVE) {
    return trans('user.deleted.name');
  }

  if (props.lastComment?.author.id === currentUser.value!.id) {
    return trans('global.you');
  }

  return props.lastComment?.author.firstname;
});
</script>

<template>
  <div
    class="last-comment-preview"
    :class="{ 'last-comment-preview__empty': !lastComment }"
  >
    <div
      v-if="!lastComment"
      class="last-comment-preview__empty-state"
    >
      <UserPicture
        v-if="currentUser"
        :user="currentUser"
        class="last-comment-preview__picture"
        :size="32"
      />

      <Typography
        variant="body-small"
        class="last-comment-preview__none"
      >
        {{ trans('selection_item.comment.add_comment') }}
      </Typography>
    </div>

    <div
      v-else
      class="last-comment-preview__details"
    >
      <UserPicture
        :user="lastComment.pipedrive_user ? lastComment.pipedrive_user : lastComment.author"
        class="last-comment-preview__picture"
        :size="32"
      />

      <div class="last-comment-preview__informations">
        <div class="last-comment-preview__top">
          <Typography variant="body-small-bold">
            {{ author }}
          </Typography>

          <Typography variant="caption" color="gray-darker">
            {{ getShortReadableDateTime(lastComment.created_at.iso, ', ') }}
          </Typography>
        </div>

        <div class="last-comment-preview__message comment-message">
          <!-- eslint-disable vue/no-v-html -->
          <p class="comment-message__body" v-html="lastComment.body" />
        </div>
      </div>
    </div>

    <Icon
      :svg="IconCaretRight"
      class="last-comment-preview__icon"
    />
  </div>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;
@use '@styles/mixins' as *;

.last-comment-preview {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: rem(8);
  padding: rem(8);
  border-radius: rem(8);
  background-color: $colorGrayLight;

  @media (hover: hover) {
    &:hover {
      background-color: $colorGray;
      cursor: pointer;
    }
  }

  &__empty-state {
    display: flex;
    align-items: center;
  }

  &__details {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: rem(8);
    padding: rem(4);
  }

  &__picture {
    flex: none;
  }

  &__informations {
    display: flex;
    flex-direction: column;
  }

  &__top {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__message, .comment-message {
    max-width: 100%;
    max-height: rem(48);
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;

    &__body {
      margin: 0;

      * {
        @include fontBodySmall;
        margin: 0;
      }

      :deep(.user-mention) * {
        @include fontBodySmallBold;
        color: $colorBlue;
      }
    }
  }

  &__icon {
    flex: none;
  }

  &__none {
    width: 100%;
    padding: rem(4) rem(12) rem(4) rem(8);
  }
}

@media screen and (min-width: $breakTablet) {
  .proposal-preview-comment {
    &__message, .comment-message {
      max-width: rem(132);
    }
  }
}

@media screen and (min-width: $breakDesktop) {
  .proposal-preview-comment {
    &__top {
      flex-direction: row;
      align-items: center;
      gap: rem(4);
    }

    &__message, .comment-message {
      max-width: rem(300);
    }
  }
}

@media screen and (min-width: $breakLargeDesktop) {
  .proposal-preview-comment {
    &__message, .comment-message {
      max-width: rem(360);
    }
  }
}
</style>
